import { In } from 'typeorm'
import { Resolver, Query, FieldResolver, Root, Args, Arg, Ctx, Directive } from 'type-graphql'
import { Domain, getQueryBuilderFromListParams, getRepository, ListParam } from '@things-factory/shell'
import { User } from '@things-factory/auth-base'
import { DataKeySet } from '../data-key-set/data-key-set.js'
import { DataItem } from '../data-set/data-item-type.js'
import { DataSet } from '../data-set/data-set.js'
import { DataSummary } from './data-summary.js'
import { DataSummaryList, DynamicDataSummary } from './data-summary-type.js'
import { queryDataSummaryByPeriod } from '../../controllers/query-data-summary-by-period.js'

@Resolver(DataSummary)
export class DataSummaryQuery {
  @Query(returns => DataSummary!, {
    nullable: true,
    description:
      'Retrieves a single data summary by its unique identifier. This query fetches aggregated statistical information about data samples including averages, counts, and other summary statistics for a specific time period.'
  })
  async dataSummary(@Arg('id') id: string, @Ctx() context: ResolverContext): Promise<DataSummary> {
    const { domain } = context.state

    return await getRepository(DataSummary).findOne({
      where: { domain: { id: domain.id }, id }
    })
  }

  @Query(returns => DataSummaryList, {
    description:
      'Retrieves multiple data summaries with pagination and filtering support. This query allows searching summaries by name and description, and is used for listing and browsing aggregated statistical data across all datasets.'
  })
  async dataSummaries(
    @Args(type => ListParam) params: ListParam,
    @Ctx() context: ResolverContext
  ): Promise<DataSummaryList> {
    const { domain } = context.state

    const queryBuilder = getQueryBuilderFromListParams({
      domain,
      params,
      repository: await getRepository(DataSummary),
      searchables: ['name', 'description']
    })

    const [items, total] = await queryBuilder.getManyAndCount()

    return { items, total }
  }

  @Query(returns => [DynamicDataSummary], {
    description:
      'Retrieves dynamically grouped data summaries for a specific time period and dataset. This query provides aggregated statistical information grouped by data keys, enabling trend analysis and performance monitoring across different dimensions.'
  })
  async dataSummaryByPeriod(
    @Arg('period') period: 'today' | 'this month' | '30 days' | 'this year' | '12 months' | 'date range' | 'month',
    @Arg('dataSetName') dataSetName: string,
    @Arg('dataKeys', type => [String], { nullable: true }) dataKeys: string[] | null,
    @Args(type => ListParam) params: ListParam,
    @Ctx() context: ResolverContext
  ): Promise<DynamicDataSummary[]> {
    return await queryDataSummaryByPeriod(period, dataSetName, dataKeys, params, context)
  }

  @Directive('@privilege(category: "data-summary", privilege: "query", domainOwnerGranted: true)')
  @Query(returns => DataSummaryList, {
    description:
      "Retrieves data summaries that belong to a specific dataset by dataset name. This query filters summaries by dataset name and includes searchable fields based on the dataset's key configuration, enabling targeted statistical analysis within a particular dataset context."
  })
  async dataSummariesByDataSetName(
    @Arg('dataSetName') dataSetName: string,
    @Args(type => ListParam) params: ListParam,
    @Ctx() context: ResolverContext
  ): Promise<DataSummaryList> {
    const { domain } = context.state

    const dataSet = await getRepository(DataSet).findOne({
      where: { domain: { id: In([domain.id, domain.parentId].filter(Boolean)) }, name: dataSetName },
      relations: ['dataKeySet']
    })

    if (!dataSet) {
      throw new Error(`dataSet not found by the given dataSetName(${dataSetName})`)
    }

    const dataKeyItems = dataSet.dataKeySet?.dataKeyItems || []
    const searchables = dataKeyItems.map((item, index) => `key0${index + 1}`)

    const queryBuilder = getQueryBuilderFromListParams({
      repository: getRepository(DataSummary),
      params,
      domain,
      alias: 'summary',
      searchables: ['name', 'description'].concat(searchables)
    }).innerJoin('summary.dataSet', 'ds', 'ds.id = :dataSetName', {
      dataSetName: dataSet.id
    })

    const [items, total] = await queryBuilder.getManyAndCount()

    return { items, total }
  }

  @Directive('@privilege(category: "data-summary", privilege: "query", domainOwnerGranted: true)')
  @Query(returns => DataSummaryList, {
    description:
      "Retrieves data summaries that belong to a specific dataset by dataset ID. This query filters summaries by dataset ID and includes searchable fields based on the dataset's key configuration, enabling targeted statistical analysis within a particular dataset context."
  })
  async dataSummariesByDataSet(
    @Arg('dataSetId') dataSetId: string,
    @Args(type => ListParam) params: ListParam,
    @Ctx() context: ResolverContext
  ): Promise<DataSummaryList> {
    const { domain } = context.state

    const dataSet = await getRepository(DataSet).findOne({
      where: { domain: { id: In([domain.id, domain.parentId].filter(Boolean)) }, id: dataSetId },
      relations: ['dataKeySet']
    })

    if (!dataSet) {
      throw new Error(`dataSet not found by the given dataSetId(${dataSetId})`)
    }

    const dataKeyItems = dataSet.dataKeySet?.dataKeyItems || []
    const searchables = dataKeyItems.map((item, index) => `key0${index + 1}`)

    const queryBuilder = getQueryBuilderFromListParams({
      repository: getRepository(DataSummary),
      params,
      domain,
      alias: 'summary',
      searchables: ['name', 'description'].concat(searchables)
    }).innerJoin('summary.dataSet', 'ds', 'ds.id = :dataSetId', {
      dataSetId
    })

    const [items, total] = await queryBuilder.getManyAndCount()

    return { items, total }
  }

  @Directive('@privilege(category: "data-summary", privilege: "query", domainOwnerGranted: true)')
  @Query(returns => DataSummaryList, {
    description:
      'Retrieves data summaries that belong to datasets using a specific data key set. This query filters summaries by key set ID and enables cross-dataset statistical analysis based on shared key configurations, useful for comparing aggregated data across related datasets.'
  })
  async dataSummariesByDataKeySet(
    @Arg('dataKeySetId') dataKeySetId: string,
    @Args(type => ListParam) params: ListParam,
    @Ctx() context: ResolverContext
  ): Promise<DataSummaryList> {
    const { domain } = context.state

    const dataKeySet = await getRepository(DataKeySet).findOneBy({ id: dataKeySetId })
    const dataKeyItems = dataKeySet?.dataKeyItems || []
    const searchables = dataKeyItems.map((item, index) => `key0${index + 1}`)

    const queryBuilder = getQueryBuilderFromListParams({
      repository: getRepository(DataSummary),
      params,
      domain,
      alias: 'summary',
      searchables: ['name', 'description'].concat(searchables)
    }).innerJoin('summary.dataSet', 'ds', 'ds.dataKeySet = :dataKeySetId', {
      dataKeySetId
    })

    const [items, total] = await queryBuilder.getManyAndCount()

    return { items, total }
  }

  @FieldResolver(type => [DataItem], {
    description:
      'Retrieves the data items (field definitions) associated with this data summary. These items define the structure and validation rules for the summary data, including field names, types, and constraints.'
  })
  async dataItems(@Root() dataSummary: DataSummary): Promise<DataItem[]> {
    const dataSet = dataSummary.dataSetId && (await getRepository(DataSet).findOneBy({ id: dataSummary.dataSetId }))

    return dataSet?.dataItems.filter(item => item.active) || []
  }

  @FieldResolver(type => DataSet, {
    description:
      'Retrieves the dataset that this data summary belongs to. This field resolver provides access to the dataset configuration and settings associated with the summary.'
  })
  async dataSet(@Root() dataSummary: DataSummary): Promise<DataSet> {
    return dataSummary.dataSetId && (await getRepository(DataSet).findOneBy({ id: dataSummary.dataSetId }))
  }

  @FieldResolver(type => Domain)
  async domain(@Root() dataSummary: DataSummary): Promise<Domain> {
    return dataSummary.domainId && (await getRepository(Domain).findOneBy({ id: dataSummary.domainId }))
  }

  @FieldResolver(type => User)
  async updater(@Root() dataSummary: DataSummary): Promise<User> {
    return dataSummary.updaterId && (await getRepository(User).findOneBy({ id: dataSummary.updaterId }))
  }

  @FieldResolver(type => User)
  async creator(@Root() dataSummary: DataSummary): Promise<User> {
    return dataSummary.creatorId && (await getRepository(User).findOneBy({ id: dataSummary.creatorId }))
  }
}
